import {
  faAnglesRight,
  faChartScatter,
  faCircle,
  faCloud,
  faCloudSlash,
  faFileLines,
  faSparkles,
  faTimes,
} from "@fortawesome/pro-regular-svg-icons";
import { faCloudCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { HocuspocusProvider } from "@hocuspocus/provider";
import * as Dialog from "@radix-ui/react-dialog";
import { reactRouterV3Instrumentation } from "@sentry/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { JSONContent } from "@tiptap/core";
import { EditorContent, useEditor } from "@tiptap/react";
import * as blockies from "blockies-ts";
import classNames from "classnames";
import ColorThief from "colorthief";
import { isEmpty, isEqual } from "lodash-es";
import { nanoid } from "nanoid";
import { findParentNodeOfType } from "prosemirror-utils";
import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import { Item, Menu } from "react-contexify";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Tooltip } from "react-tooltip";
import { Edge, Node, ReactFlowProvider } from "reactflow";
import { format } from "timeago.js";
import useLocalStorageState from "use-local-storage-state";
import { useDebounce, useInterval } from "usehooks-ts";
import { IndexeddbPersistence } from "y-indexeddb";
import {
  prosemirrorJSONToYXmlFragment,
  yDocToProsemirrorJSON,
  yXmlFragmentToProsemirrorJSON,
} from "y-prosemirror";
import * as Y from "yjs";

import { Graph, LLMSidebar, MenuBar, Versions } from "@/components";
import { loadExtensions } from "@/extensions";
import { useActiveEditor, useCeramic, useQueries, YdocProvider } from "@/hooks";
import { getDefaultPageId, getPage, getPageIds } from "@/hooks/useQueries/pages";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import { errorMessage, rgbToHex } from "@/utils";

import { getEditorNodes, getJsonBlockIds } from "../Graph/utils";
import { setNodePageJson } from "../LLMSidebar/utils";
import data from "./coordination-longevity-tech-tree-2024-01-15.json";
import styles from "./Page.module.scss";
import SemanticBuddy from "./SemanticBuddy";
import longevity from "./tt-data/longevity.json";
import binUrl from "./ydoc2.bin?url";

const colorThief = new ColorThief();

interface PageProps extends React.SelectHTMLAttributes<HTMLDivElement> {
  id: string | undefined;
  nodePageOpen?: boolean;
  isDefault?: boolean;
  isNodePage?: boolean;
  setSyncedNodeSelectVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

export default forwardRef<HTMLDivElement, PageProps>(
  (
    {
      id,
      isDefault = false,
      className,
      nodePageOpen = false,
      isNodePage = false,
      setSyncedNodeSelectVisible,
      ...props
    },
    ref
  ) => {
    let { projectId = "global-graph", pageId } = useParams();
    const { address } = useCeramic();
    const queryClient = useQueryClient();

    const { activeEditor, setActiveEditor, id: activeId, setId } = useActiveEditor();
    const { isMobile } = useWindowDimensions();

    const { projects, currentProject, createPage, updatePage } = useQueries();
    const [llmOpen, setLlmOpen] = useLocalStorageState<boolean>(
      `ldg-${currentProject?.slug}-llm-open${isNodePage ? `-${id}` : ""}`,
      { defaultValue: !isNodePage ? true : false }
    );
    const [graphOpen, setGraphOpen] = useLocalStorageState<boolean>(
      `ldg-${currentProject?.slug}-graph-open${isNodePage ? `-${id}` : ""}`,
      { defaultValue: !isNodePage ? false : false }
    );
    const [editorOpen, setEditorOpen] = useLocalStorageState<boolean>(
      `ldg-${currentProject?.slug}-editor-open${isNodePage ? `-${id}` : ""}`,
      { defaultValue: !isNodePage ? true : true }
    );

    const [copiedNode, setCopiedNode] = useLocalStorageState<{
      syncedBlockId: string;
      syncedBlockProject: string;
    }>(`ldg-copied-synced-node`, {
      defaultValue: { syncedBlockId: "", syncedBlockProject: "" },
    });

    const {
      data: page,
      isLoading,
      isError,
    } = useQuery({
      queryKey: ["pages", currentProject?.id, id],
      queryFn: ({ signal }) => getPage(signal, currentProject?.id, id),
      enabled: Boolean(currentProject?.id && id),
      retry: false,
    });

    const { data: pageIds } = useQuery({
      queryKey: ["page-ids"],
      queryFn: ({ signal }) => getPageIds(signal, currentProject?.id),
      enabled: Boolean(currentProject?.id),
      initialData: [],
    });

    const [editorState, setEditorState] = useState<JSONContent>({});

    useEffect(() => {
      if (isError) {
        console.log("Page not found, so creating it");
        createPage({ lateralID: id ? id : nanoid() });
      }
    }, [isError, id]);

    const [providerSynced, setProviderSynced] = useState<boolean>(false);
    const [providerConnected, setProviderConnected] = useState<boolean>(false);
    const roomName = useMemo(
      () => (currentProject?.slug && id ? `lateral-ldg-${currentProject?.slug}-${id}` : undefined),
      [currentProject, id]
    );
    const ydoc = useMemo(() => (roomName ? new Y.Doc({ guid: roomName }) : undefined), [roomName]);
    const provider = useMemo(() => {
      if (!roomName || !ydoc) return;
      // Set up the Indexeddb and Hocuspocus WebSocket provider
      // new IndexeddbPersistence(roomName, ydoc);
      return new HocuspocusProvider({
        url: import.meta.env.VITE_HOCUSPOCUS_URL,
        name: roomName,
        document: ydoc,
        onSynced() {
          setProviderSynced(true);
        },
        onStatus(data) {
          setProviderConnected(data.status == "connected");
        },
      });
    }, [roomName]);
    const extensions = loadExtensions(provider, ydoc);

    const emptyList: JSONContent = {
      type: "bulletList",
      content: [
        {
          type: "listItem",
          content: [{ type: "paragraph" }],
        },
      ],
    };

    const editor = useEditor(
      {
        // autofocus: "start",
        // @ts-ignore bit hacky but works for now
        editorProps: { pageId: id },
        autofocus: "start",
        onFocus: ({ editor }) => {
          setActiveEditor(editor);
          setId(id);
        },
        onUpdate: ({ editor }) => {
          setActiveEditor(editor);
          const content = editor?.getJSON();
          if (content) {
            setEditorState(content);
          }
        },
        onDestroy: () => {
          setActiveEditor(undefined);
          setId(undefined);
        },
        onCreate: async ({ editor }) => {
          if (isLoading || !page) return;
          setEditorState(page?.content);
          setActiveEditor(editor);
          setId(id);
          setEditorReady(true);
        },
        extensions,
      },
      [id, isLoading]
    );

    useEffect(() => {
      if (address && editor && editor.commands.updateUser) {
        const image = blockies.create({ seed: address.toLowerCase() }).toDataURL();
        const img = document.createElement("img");
        img.src = image;
        img.addEventListener("load", function () {
          const [r, g, b] = colorThief.getColor(img);
          editor.commands.updateUser({
            address,
            image: image,
            color: rgbToHex(r, g, b),
          });
        });
      }
    }, [address, editor]);

    const debouncedEditor = useDebounce(editorState, 1000);

    const [lastSaved, setLastSaved] = useState<Date>(new Date());
    const [lastSavedAgo, setLastSavedAgo] = useState<string>(format(lastSaved));
    const [editorReady, setEditorReady] = useState<boolean>(false);
    const [versionsTooLarge, setVersionsTooLarge] = useState<boolean>(false);

    useInterval(() => {
      setLastSavedAgo(format(lastSaved));
    }, 1000);

    const createSaveData = useCallback(() => {
      if (!editor) return;
      let nodes = Array.from(ydoc?.getMap("nodes").values()) as Node[];
      const { ids } = getEditorNodes(editor);
      nodes = nodes.filter((n) => ids.includes(n.id));
      let edges = Array.from(ydoc?.getMap("edges").values()) as Edge[];
      edges = edges.filter((e) => ids.includes(e.source) || ids.includes(e.target));
      return { editorState, nodes, edges };
    }, [ydoc, editor, editorState]);

    useInterval(() => {
      const saveData = createSaveData();
      if (!page || isEmpty(editorState) || isEqual(saveData, page?.content)) {
        console.log("No changes detected.");
        return;
      } else {
        console.log("Saving version");
      }
      try {
        createPage({
          isDefault: page?.isDefault,
          lateralID: page?.id,
          originalAuthor: page?.originalAuthor,
          meta: JSON.stringify(page?.meta ?? "{}"),
          content: JSON.stringify(saveData),
        });
      } catch (error) {
        const message = errorMessage(error);
        if (message.includes("exceeds the maximum block size")) {
          setVersionsTooLarge(true);
        }
      }
      setLastSaved(new Date());
      setLastSavedAgo(format(lastSaved));
      // }, 5000);
    }, 1000 * 60 * 5);

    const saveState = async () => {
      const saveData = createSaveData();
      console.log("saveData", saveData);
      if (
        !page ||
        isEmpty(editorState) ||
        JSON.stringify(saveData) == JSON.stringify(page?.content)
      )
        return;
      try {
        await updatePage({
          page,
          content: { content: JSON.stringify(saveData) },
        });
      } catch (error) {
        const message = errorMessage(error);
        if (message.includes("exceeds the maximum block size")) {
          setVersionsTooLarge(true);
        }
      }
      console.log("Saved page in saveState", saveData);
      setLastSaved(new Date());
      setLastSavedAgo(format(lastSaved));
      queryClient.invalidateQueries({ queryKey: ["pages"] });
      queryClient.invalidateQueries({ queryKey: ["page-ids"] });
      queryClient.refetchQueries({ queryKey: ["project-tokens"] });
    };

    useEffect(() => {
      if (debouncedEditor && page && !isEqual(page.content, editorState)) {
        saveState();
      }
    }, [debouncedEditor]);

    useEffect(() => {
      if (pageIds.length && editorReady) {
        editor?.state.doc.descendants((node, pos) => {
          if (node.attrs.blockId && node.type.name == "listItem") {
            const transaction = editor?.state.tr.setNodeAttribute(
              pos,
              "hasNodePage",
              Boolean(pageIds.includes(node.attrs.blockId))
            );
            if (transaction.docChanged) {
              transaction.setMeta("addToHistory", false);
              editor.view.dispatch(transaction);
            }
          }
        });
      }
    }, [pageIds, editorReady]);

    const handleItemClick = ({ id, event, props }) => {
      if (!editor?.view.state.selection) return;
      const node = findParentNodeOfType(editor?.schema.nodes.listItem)(
        editor?.view.state.selection
      );
      const listItem = node?.node;
      switch (id) {
        case "copy-synced":
          toast.success("Node copied");
          setCopiedNode({
            syncedBlockId: listItem?.attrs?.blockId,
            syncedBlockProject: currentProject?.slug ?? "",
          });

          break;
        case "paste-synced":
          if (!copiedNode) return;
          console.log("paste");
          const selection = activeEditor?.view.state.selection;
          const range = selection?.ranges[0];
          if (!range) return;
          console.log("selection", selection);
          console.log("range", range);
          console.log("copiedNode", copiedNode);

          activeEditor
            ?.chain()
            .focus()
            .insertContentAt(
              { from: range.$from.pos - 2, to: range.$to.pos },
              {
                type: "listItem",
                attrs: copiedNode,
                content: [{ type: "paragraph", content: [] }],
              }
            )
            .run();
          break;
        case "insert-synced":
          setSyncedNodeSelectVisible(true);
          break;
      }
    };

    // const cloneProject = async (slug: string) => {
    //   const project = projects?.find((p) => p.slug === slug);
    //   if (!project) return;
    //   const defaultPageId = await getDefaultPageId(undefined, project.id);
    //   const roomName = `lateral-ldg-${project?.slug}-${defaultPageId}`;
    //   const otherYdoc = new Y.Doc({ guid: roomName });
    //   const provider = new HocuspocusProvider({
    //     url: import.meta.env.VITE_HOCUSPOCUS_URL,
    //     name: roomName,
    //     document: otherYdoc,
    //   });
    //   provider.on("sync", () => {
    //     console.log("we have data!");
    //     // const xml = otherYdoc?.getXmlFragment("default");
    //     const json = yDocToProsemirrorJSON(otherYdoc, "default");
    //     editor?.commands?.setContent(json);

    //     const nodes = Array.from(otherYdoc?.getMap("nodes").values());
    //     const nodesMap = ydoc?.getMap<Node>("nodes");
    //     nodes.forEach((node) => {
    //       nodesMap.set(node.id, node);
    //     });

    //     const edges = Array.from(otherYdoc?.getMap("edges").values());
    //     const edgesMap = ydoc?.getMap<Edge>("edges");
    //     edges.forEach((edge) => {
    //       edgesMap.set(edge.id, edge);
    //     });
    //     console.log(json, nodes, edges);
    //   });
    // };

    // useEffect(() => {
    //   if (!projects.length || !ydoc) return;
    //   cloneProject("longevity-tech-tree");
    // }, [projects, ydoc]);

    // const loadCrdt = async () => {
    //   const response = await django.get(`editor/node/roadmap/`, {
    //     responseType: "arraybuffer",
    //     params: {
    //       crdt: 1,
    //     },
    //   });
    //   // console.log("data", response.data);
    //   // const binaryEncoded = toUint8Array(base64YDoc);
    //   // const buffer = response.arrayBuffer();
    //   // .then(buffer => console.log(new DataView(buffer).getUint16()));
    //   const deserializedYDoc = new Y.Doc();
    //   console.log(response);
    //   console.log("m", Y.mergeUpdates([new Uint8Array(response.data)]));
    //   // Y.logUpdate(new Uint8Array(response.data));
    //   Y.applyUpdate(
    //     deserializedYDoc,
    //     Y.mergeUpdates([new Uint8Array(response.data)])
    //   );
    //   const json = yDocToProsemirrorJSON(deserializedYDoc, "default");
    //   console.log(json);
    //   editor.commands.setContent(json);
    //   console.log(deserializedYDoc);
    //   console.log("doc", deserializedYDoc.getXmlFragment("default").toJSON());
    // };

    // useEffect(() => {
    //   loadCrdt();
    // }, []);

    // const loadFromJson = async () => {
    //   // editor.commands.setContent(json);
    //   console.log(data);
    //   const pages = data.pages;
    //   const defaultPages = data.pages.filter((p) => p.isDefault);
    //   defaultPages.forEach((p) => {
    //     console.log(p.content.nodes);
    //   });
    // };

    // useEffect(() => {
    //   loadFromJson();
    // }, []);

    // const loadProject = async () => {
    //   console.log("HELLO");
    //   const bin = new Uint8Array(await (await fetch(binUrl)).arrayBuffer());
    //   const deserializedYDoc = new Y.Doc();
    //   console.log(deserializedYDoc);
    //   console.log("m", Y.mergeUpdates([bin]));
    //   Y.applyUpdate(deserializedYDoc, Y.mergeUpdates([bin]));
    //   console.log("deserializedYDoc", deserializedYDoc);
    //   const nodes = Array.from(deserializedYDoc.getMap<Node>("nodes").values());
    //   const edges = Array.from(deserializedYDoc.getMap<Node>("edges").values());
    //   console.log(nodes, edges);
    //   const json = yDocToProsemirrorJSON(deserializedYDoc, "default");
    //   const ids = getJsonBlockIds(json.content);
    //   const filteredNodes = nodes.filter((n) => ids.includes(n.id));
    //   console.log(filteredNodes);
    //   const filteredEdges = edges.filter((e) => ids.includes(e.source) || ids.includes(e.target));
    //   console.log(filteredEdges);
    //   console.log(json);
    //   console.log(ids.length);

    //   editor?.commands.setContent(json);

    //   const nodesMap = ydoc?.getMap<Node>("nodes");
    //   filteredNodes.forEach((node) => {
    //     nodesMap.set(node.id, node);
    //   });

    //   const edgesMap = ydoc?.getMap<Edge>("edges");
    //   filteredEdges.forEach((edge) => {
    //     edgesMap.set(edge.id, edge);
    //   });

    //   // console.log(data.pages);

    //   const pages = data.pages
    //     .filter((p) => ids.includes(p.id))
    //     .forEach((p) => {
    //       if (typeof p.content == "object") {
    //         if ("editorState" in p.content) {
    //           const roomName = `lateral-ldg-${currentProject?.slug}-${p?.id}`;
    //           console.log(p?.content?.editorState);
    //           try {
    //             setNodePageJson(p?.content?.editorState["content"], roomName, editor?.schema);
    //           } catch (e) {
    //             console.log("ERROR SAVING", p.id, e);
    //           }
    //         }
    //       }
    //     });

    //   // console.log(pages);
    //   // // editor.commands.setContent(json);
    //   // console.log(deserializedYDoc);
    //   // console.log("doc", deserializedYDoc.getXmlFragment("default").toJSON());
    //   // yXmlFragmentToProsemirrorJSON
    //   // prosemirrorJSONToYXmlFragment
    // };

    // useEffect(() => {
    //   if (!editor || !isDefault) return;
    //   provider?.on("sync", () => {
    //     // loadProject();
    //   });
    // }, [editor, provider]);

    if (!page || !editor || isLoading || !providerSynced) {
      return (
        <div {...props} className={classNames(className, styles.border)}>
          <div className={styles.loader}>Loading...</div>
        </div>
      );
    }

    return (
      <div
        {...props}
        className={classNames(className)}
        style={
          nodePageOpen && isNodePage
            ? {
                width:
                  (editorOpen ? 1 : 0) + (llmOpen ? 1 : 0) + (graphOpen ? 1 : 0) > 1
                    ? "75%"
                    : "40%",
              }
            : {}
        }
      >
        <YdocProvider ydoc={ydoc}>
          <ReactFlowProvider>
            {isNodePage && (
              <div className={styles.close}>
                <Tooltip id="close" content="Close node page" className="tooltip" />
                <Link to={`/project/${currentProject?.slug}`} data-tooltip-id="close">
                  <FontAwesomeIcon icon={faAnglesRight} />
                </Link>
              </div>
            )}
            {isDefault && (
              <div
                className={classNames(styles.connectionStatus, {
                  [styles.disconnected]: !providerConnected,
                  [styles.connected]: providerConnected,
                })}
                style={{ left: (document?.getElementById("headerLeft")?.clientWidth ?? 0) + 32 }}
              >
                {providerConnected ? (
                  <>
                    <Tooltip id="conn" content="Connected to sync server" className="tooltip" />
                    <FontAwesomeIcon icon={faCloudCheck} data-tooltip-id="conn" />
                  </>
                ) : (
                  <>
                    <Tooltip
                      id="notconn"
                      content="Not connected to sync server, try refreshing"
                      className="tooltip"
                    />
                    <FontAwesomeIcon icon={faCloudSlash} data-tooltip-id="notconn" />
                  </>
                )}
              </div>
            )}
            <div
              className={classNames(styles.paneToggles, {
                [styles.nodePageOpen]: nodePageOpen,
                [styles.isNodePage]: isNodePage,
              })}
              style={
                nodePageOpen && !isNodePage
                  ? // Hacky, yes but it works
                    { left: (document?.getElementById("headerLeft")?.clientWidth ?? 0) + 32 + 46 }
                  : {}
              }
            >
              <Tooltip id="editor" content="Toggle Editor" className="tooltip" />
              <button
                onClick={() => setEditorOpen(!editorOpen)}
                data-tooltip-id="editor"
                className={classNames({ [styles.active]: editorOpen })}
              >
                <FontAwesomeIcon icon={faFileLines} />
              </button>
              <div className={styles.divider}></div>
              <Tooltip id="graph" content="Toggle Graph" className="tooltip" />
              <button
                onClick={() => setGraphOpen(!graphOpen)}
                data-tooltip-id="graph"
                className={classNames({ [styles.active]: graphOpen })}
              >
                <FontAwesomeIcon icon={faChartScatter} />
              </button>
              <div className={styles.divider}></div>
              <Tooltip id="llm" content="Toggle LLM" className="tooltip" />
              <button
                onClick={() => setLlmOpen(!llmOpen)}
                data-tooltip-id="llm"
                className={classNames({ [styles.active]: llmOpen })}
              >
                <FontAwesomeIcon icon={faSparkles} />
              </button>
            </div>
            <div className={classNames(styles.columns)}>
              {editorOpen && (
                <div className={classNames(styles.wrapper, styles.editor)}>
                  <div className={classNames(styles.scroll, styles.editorWrapper)}>
                    <MenuBar editor={editor} page={page} />
                    <EditorContent editor={editor} key={`${projectId}-${id}`} />
                  </div>

                  <Menu id="ListItem" animation="">
                    <Item id="insert-synced" onClick={handleItemClick}>
                      Insert synced node
                    </Item>
                    <Item id="copy-synced" onClick={handleItemClick}>
                      Copy synced node
                    </Item>
                    <Item id="paste-synced" onClick={handleItemClick}>
                      Paste synced node
                    </Item>
                  </Menu>
                  <Dialog.Root>
                    <footer className={styles.editorFooter}>
                      <div className={styles.left}>
                        {versionsTooLarge && (
                          <>
                            <Tooltip
                              id="errVersions"
                              content="This project is too large and we're therefore unable to make backups of it"
                              className="tooltip"
                            />
                            <span data-tooltip-id="errVersions" className={styles.versionsError}>
                              Error saving versions!
                            </span>
                            <div className={styles.divider}></div>
                          </>
                        )}
                        <Dialog.Trigger asChild>
                          <button>Versions</button>
                        </Dialog.Trigger>
                        <div className={styles.divider}></div>
                        Last Saved: {lastSavedAgo}
                      </div>
                      <div>
                        <SemanticBuddy editor={editor} />
                        <button
                          style={{ display: "none" }}
                          onClick={() => {
                            console.log("Hello!");
                            editor.commands.setContent(longevity);
                          }}
                        >
                          Tech Trees
                        </button>
                      </div>
                    </footer>

                    <Dialog.Portal>
                      <Dialog.Overlay className={styles.dialogOverlay} />
                      <Dialog.Content
                        className={classNames(styles.dialogContent, styles.systemMessage)}
                      >
                        <Dialog.Close asChild>
                          <button className={styles.iconButton} aria-label="Close">
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </Dialog.Close>
                        <ReactFlowProvider>
                          <Versions id={id} editor={editor} project={currentProject} />
                        </ReactFlowProvider>
                      </Dialog.Content>
                    </Dialog.Portal>
                  </Dialog.Root>
                </div>
              )}

              {graphOpen && (
                <Graph
                  pageId={id}
                  className={classNames(styles.wrapper)}
                  editor={editor}
                  ydoc={ydoc}
                  key={roomName}
                  active={id == activeId}
                  provider={provider}
                />
              )}

              {llmOpen && (
                <LLMSidebar
                  className={classNames(styles.wrapper, styles.scroll)}
                  ydoc={ydoc}
                  editor={editor}
                />
              )}
            </div>
          </ReactFlowProvider>
        </YdocProvider>
      </div>
    );
  }
);
